/* eslint-disable max-lines */
import axios from 'axios';
import HomeNavBar from '@/components/HomeNavBar/HomeNavBar.vue';
import {
    add_to_cart as add_to_cart_helper,
    extract_selected_attribute_ids
} from '@/components/cart/cart_helper';
import { get_token, get_user_details } from '@/router';
import Swal from "sweetalert2";
import { get_active_organization_id, is_enabled_feature } from "@/utils/organization_profile";
import { view_profile as view_profile_helper } from "@/views/Feed/feed_helper.js";
import {
    validate_service_request,
    view_profile_product,
    get_service_request_prefill,
    sanitize_service_request_phone_number,
    normalize_service_request_date,
    build_service_availability_days,
    get_service_availability_month_label,
    get_service_availability_times,
    format_service_slot_label,
    build_service_request_datetime,
    is_service_request_slot_disabled,
    display_marketplace_price,
    format_price as format_marketplace_price,
    has_active_marketplace_product_sale,
    get_marketplace_original_price,
    get_marketplace_sale_price,
    get_marketplace_sale_discount_label,
    get_marketplace_company_logo,
    get_marketplace_company_name,
    get_marketplace_company_initials,
    group_marketplace_attributes,
    get_required_marketplace_attribute_groups,
    select_marketplace_attribute,
    validate_marketplace_required_attributes,
    get_marketplace_product_base_profile_id,
    prepare_marketplace_buy_now_checkout,
    get_displayed_marketplace_products,
    get_marketplace_display_description
} from "@/views/Marketplace/marketplace_helpers";
import {
    auto_select_single_product_attribute_options,
    is_valid_product_attribute_option
} from "@/views/ProductView/product_view_helpers";
import { marketplace_listing_realtime_methods } from "@/views/Marketplace/marketplace_realtime_helper";
import { HOME_FEATURE_ICONS } from "@/utils/home_feature_icons";

const MARKETPLACE_PRODUCTS_CACHE_TTL_MS = 5 * 60 * 1000;
const MARKETPLACE_PRODUCTS_PAGE_SIZE = 10;
const marketplace_products_cache = new Map();
const marketplace_products_inflight = new Map();

function clone_marketplace_response(value) {
    if (!value) return value;
    return JSON.parse(JSON.stringify(value));
}

function build_marketplace_cache_key(params = {}) {
    const normalized_params = Object.keys(params)
        .sort()
        .reduce((accumulator, key) => {
            const value = Reflect.get(params, key);
            if (value !== undefined && value !== null && value !== '') {
                Reflect.set(accumulator, key, String(value));
            }
            return accumulator;
        }, {});

    return JSON.stringify(normalized_params);
}

function get_cached_marketplace_products(params = {}) {
    const key = build_marketplace_cache_key(params);
    const cached = marketplace_products_cache.get(key);

    if (!cached) return null;

    if (Date.now() > cached.expires_at) {
        marketplace_products_cache.delete(key);
        return null;
    }

    return clone_marketplace_response(cached.response);
}

function set_cached_marketplace_products(params = {}, response) {
    const key = build_marketplace_cache_key(params);
    marketplace_products_cache.set(key, {
        response: clone_marketplace_response(response),
        expires_at: Date.now() + MARKETPLACE_PRODUCTS_CACHE_TTL_MS
    });
}

async function fetch_cached_marketplace_products(params = {}) {
    const cached = get_cached_marketplace_products(params);
    if (cached) return cached;

    const key = build_marketplace_cache_key(params);
    if (marketplace_products_inflight.has(key)) {
        return clone_marketplace_response(await marketplace_products_inflight.get(key));
    }

    const request = axios.get(
        `${process.env.VUE_APP_BACKEND_API_URL}/ads_management/get_marketplace_products/`,
        { params }
    ).then((response) => {
        if (response.data?.status === 'success') {
            set_cached_marketplace_products(params, response.data);
        }
        return response.data;
    }).finally(() => {
        marketplace_products_inflight.delete(key);
    });

    marketplace_products_inflight.set(key, request);
    return clone_marketplace_response(await request);
}

export default {
    name: 'Marketplace',
    components: {
        HomeNavBar
    },
    inject: ['client_features'],
    data() {
        return ({
            errors: {},
            show_menu: false,
            departments: [],
            categories: [],
            subcategories: [],
            all_categories: [],
            all_subcategories: [],
            hovered_department_id: null,
            loading: true,
            error: null,
            filters: {
                search: '',
                price_range: '',
                supplier_id: '',
                department_id: '',
                category_id: '',
                subcategory_id: '',
            },
            pagination: {
                current_page: 1,
                total_pages: 1,
                next_page: null,
                prev_page: null,
                total_count: 0,
                has_more: true
            },
            services: [],
            loading_services: false,
            pagination_services: {
                current_page: 1,
                total_pages: 1,
                has_more: false,
                total_count: 0
            },
            selected_product: null,

            service_request: {
                message: '',
                email: '',
                contact_number: '',
                date: '',
                product_id: null
            },
            service_availability_week_offset: 0,
            selected_service_request_day_key: '',
            selected_service_availability_month: '',
            products: [],
            loading_products: false,
            pagination_products: {
                current_page: 1,
                total_pages: 1,
                has_more: false,
                total_count: 0
            },
            auto_paginate_without_scroll: true,
            auto_paginate_token: 0,
            auto_paginate_timeout_id: null,
            infinite_scroll_loading: false,
            intersection_observer: null,
            active_image_index: 0,
            product_reviews: [],
            new_rating: 0,
            hover_rating: 0,
            product_review_text: '',
            selected_quantity: 1,
            user_has_rated_product: false,
            user_details: get_user_details() || {},
            wishlisted_product_ids: [],
            showing_wishlist: false,
            active_listing_type: 'products',
            selected_supplier: null,
            supplier_source_products: [],
            selected_attributes: {},
            attribute_errors: {},
            isLoading: false,
            token_exists: null,

        });
    },
    watch: {
        wishlisted_product_ids() {
            this.sync_wishlist_state();
        },
        products() {
            this.sync_wishlist_state();
        }
    },
    computed: {
        home_feature_icons() {
            return HOME_FEATURE_ICONS;
        },

        visible_suppliers() {
            const supplier_map = new Map();
            const supplier_products = this.supplier_source_products.length
                ? this.supplier_source_products
                : this.products;

            this.filter_products_by_category(this.filter_out_my_listings(supplier_products || [])).forEach((product) => {
                const supplier_entry = this.build_visible_supplier(product);

                if (!supplier_entry || supplier_map.has(String(supplier_entry.id))) return;

                supplier_map.set(String(supplier_entry.id), supplier_entry);
            });

            return Array.from(supplier_map.values()).sort((a, b) =>
                a.company_name.localeCompare(b.company_name)
            );
        },

        selected_department_name() {
            const match = this.departments.find(
                (department) => String(department.id) === String(this.filters.department_id)
            );
            return match ? match.name : '';
        },

        selected_supplier_name() {
            return this.selected_supplier?.company_name || '';
        },

        selected_category_name() {
            const match = this.categories.find(
                (category) => String(category.id) === String(this.filters.category_id)
            );
            return match ? match.name : '';
        },

        selected_subcategory_name() {
            const match = this.subcategories.find(
                (subcategory) => String(subcategory.id) === String(this.filters.subcategory_id)
            );
            return match ? match.name : '';
        },

        marketplace_category_tiles() {
            return this.categories?.length
                ? this.categories
                : this.all_categories;
        },

        marketplace_subcategory_tiles() {
            return this.subcategories?.length
                ? this.subcategories
                : this.get_subcategories_for_category(this.filters.category_id);
        },
        price_range_label() {
            const labels = {
                '0-1000': 'Under R1,000',
                '1000-5000': 'R1,000 - R5,000',
                '5000-20000': 'R5,000 - R20,000',
                '20000-plus': 'Over R20,000'
            };

            return labels[this.filters.price_range] || '';
        },

        results_heading() {
            if (this.showing_wishlist) return 'My Wishlist';
            if (this.selected_supplier_name) return this.selected_supplier_name;
            if (this.selected_subcategory_name) return this.selected_subcategory_name;
            if (this.selected_category_name) return this.selected_category_name;
            if (this.selected_department_name) return this.selected_department_name;
            if (this.filters.search && this.filters.search.trim()) return 'Search Results';

            return 'All Products & Services';
        },

        displayed_products() {
            return get_displayed_marketplace_products({
                listing_type: this.active_listing_type,
                products: this.products || [],
                services: this.services || [],
                filter_out_my_listings: (listings) => this.filter_out_my_listings(listings),
                filter_products_by_category: (listings) => this.filter_products_by_category(listings),
            });
        },

        // Switch the loading state based on what tab is active
        is_loading() {
            return this.active_listing_type === 'services'
                ? this.loading_services
                : this.loading_products;
        },

        // Switch the pagination state
        current_pagination() {
            return this.active_listing_type === 'services'
                ? this.pagination_services
                : this.pagination_products;
        },

        min_date() {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');

            // Returns "YYYY-MM-DDTHH:MM"
            return `${year}-${month}-${day}T${hours}:${minutes}`;
        },
        active_item() {
            return this.selected_product || this.service_request;
        },

        active_images() {
            return this.active_item?.images || [];
        },

        selected_product_total_price() {
            const unit_price = Number(this.display_price(this.selected_product) || 0);
            const quantity = Number(this.selected_quantity) || 1;
            return unit_price * quantity;
        },

        is_selected_product_quantity_disabled() {
            return this.is_my_marketplace_product(this.selected_product);
        },

        service_availability_days() {
            return build_service_availability_days(
                this.service_availability_week_offset,
                5,
                this.selected_service_availability_month
            );
        },

        service_availability_month_label() {
            return get_service_availability_month_label(this.service_availability_days);
        },

        service_availability_times() {
            return get_service_availability_times();
        },

        min_service_availability_month() {
            const now = new Date();
            return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        },

        selected_service_request_day() {
            return this.service_availability_days.find(
                (day) => day.date_key === this.selected_service_request_day_key
            ) || this.service_availability_days[0];
        },
    },
    mounted() {
        this.token_exists = get_token();
        this.user_details = get_user_details() || {};
        this.fetch_marketplace_departments();
        this.fetch_marketplace_services();
        this.fetch_marketplace_products()
        window.addEventListener("realtime:event", this.handle_marketplace_realtime_event);

        // Set up infinite scroll observer
        this.$nextTick(() => {
            this.setup_infinite_scroll();
        });
        $('#product_details_modal').on('hidden.bs.modal', () => {
            this.reset_product_select_state();
        });
    },
    beforeUnmount() {
        if (this.auto_paginate_timeout_id) {
            clearTimeout(this.auto_paginate_timeout_id);
            this.auto_paginate_timeout_id = null;
        }
        window.removeEventListener("realtime:event", this.handle_marketplace_realtime_event);

        if (this.intersection_observer) {
            this.intersection_observer.disconnect();
        }
    },
    methods: {
        ...marketplace_listing_realtime_methods,
        display_price(product) {
            return display_marketplace_price(product);
        },
        get_display_description(value) {
            return get_marketplace_display_description(value);
        },
        should_show_product_sale(product) {
            return has_active_marketplace_product_sale(product);
        },
        get_product_original_price(product) {
            return get_marketplace_original_price(product);
        },
        get_product_sale_price(product) {
            return get_marketplace_sale_price(product);
        },
        get_product_sale_discount_label(product) {
            return get_marketplace_sale_discount_label(product);
        },
        get_user_location() {
            return new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(async (position) => {
                    try {
                        const res = await axios.get('https://nominatim.openstreetmap.org/reverse', {
                            params: { format: 'jsonv2', lat: position.coords.latitude, lon: position.coords.longitude }
                        });

                        const addr = res.data.address;
                        const cleanRoad = addr.road ? addr.road.replace(/road|rd|street|st|avenue|ave/gi, '').trim() : "";
                        this.filters.road = cleanRoad;
                        this.filters.suburb = addr.suburb || addr.neighbourhood || "";
                        this.filters.city = addr.city || addr.town || addr.village || "";
                        this.filters.municipality = addr.municipality || "";
                        this.filters.state = addr.state || "";

                        resolve();
                    } catch (err) { reject(err); }
                }, (err) => reject(err), { timeout: 10000 });
            });
        },
        auto_complete_map() {
            if (typeof window.google === 'undefined' || !window.google.maps) {
                console.warn("Google Maps not loaded yet.");
                return;
            }
            const inputElement = this.$refs.marketplaceLocationInput;
            if (!inputElement) return;
            const autocomplete = new window.google.maps.places.Autocomplete(inputElement, {
                types: ['address'] // physical addresses only
            });

            autocomplete.addListener('place_changed', () => {
                const place = autocomplete.getPlace();
                if (place && place.formatted_address) {
                    this.service_request.location = place.formatted_address;
                }
            });
        },
        sync_service_request_field(field_name = '') {
            if (field_name === 'contact_number') {
                this.service_request.contact_number = sanitize_service_request_phone_number(
                    this.service_request?.contact_number
                );
            }

            if (field_name === 'date') {
                this.service_request.date = normalize_service_request_date(
                    this.service_request?.date,
                    this.min_date
                );
            }

            if (field_name === 'email' && this.errors?.email) {
                this.errors = { ...this.errors, email: '' };
            }

            if (field_name === 'contact_number' && this.errors?.contact_number) {
                this.errors = { ...this.errors, contact_number: '' };
            }

            if (field_name === 'location' && this.errors?.location) {
                this.errors = { ...this.errors, location: '' };
            }

            if (field_name === 'date' && this.errors?.service_date) {
                this.errors = { ...this.errors, service_date: '' };
            }
        },
        select_service_request_slot(day, time) {
            if (this.is_service_request_slot_disabled(day, time)) return;

            this.selected_service_request_day_key = day.date_key;
            this.service_request.date = build_service_request_datetime(day, time);
            this.sync_service_request_field('date');
        },
        select_service_request_day(day) {
            const was_selected_day = this.is_service_request_day_selected(day);
            this.selected_service_request_day_key = day.date_key;

            if (this.service_request?.date && !was_selected_day) {
                this.service_request.date = '';
            }
        },
        change_service_availability_week(direction) {
            const next_offset = this.service_availability_week_offset + direction;
            this.service_availability_week_offset = Math.max(0, next_offset);
            this.selected_service_request_day_key = this.service_availability_days[0]?.date_key || '';
        },
        select_service_availability_month(event) {
            const month_value = event?.target?.value || '';
            if (!month_value) return;

            this.selected_service_availability_month = month_value;
            this.service_availability_week_offset = 0;
            this.selected_service_request_day_key = this.service_availability_days[0]?.date_key || '';
            this.service_request.date = '';
        },
        is_service_request_day_selected(day) {
            return this.selected_service_request_day?.date_key === day.date_key;
        },
        is_service_request_slot_selected(day, time) {
            return this.service_request?.date === build_service_request_datetime(day, time);
        },
        is_service_request_slot_disabled(day, time) {
            return is_service_request_slot_disabled(day, time, this.min_date);
        },
        format_service_slot_label(time) {
            return format_service_slot_label(time);
        },
        submit_service_request() {
            if (this.is_my_marketplace_product(this.service_request)) return;
            this.sync_service_request_field('contact_number');
            this.sync_service_request_field('date');
            const validation = validate_service_request(this.service_request);

            if (!validation.valid) {
                this.errors = validation.errors;
                return;
            }

            this.errors = {};

            let formData = new FormData();
            formData.append('message', this.service_request.message);
            formData.append('company_base_profile', this.service_request?.base_profile.id);
            formData.append('email', String(this.service_request.email || '').trim().toLowerCase());
            formData.append('contact_number', this.service_request.contact_number);
            formData.append('request_date', this.service_request.date);
            formData.append('product_id', this.service_request.product_id);
            formData.append('location', this.service_request.location || null);

            this.isLoading = true;
            $.ajax({
                url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/request_service/`,
                method: 'POST',
                headers: {
                    'Authorization': `Token ${this.getToken()}`,
                },
                data: formData,
                processData: false,
                contentType: false,
                success: (response) => {
                    if (response.status === "success") {
                        $('#service_request_modal').modal('hide');
                        Swal.fire({
                            icon: 'success',
                            title: 'Success',
                            text: 'Service request submitted! Redirecting...',
                            showConfirmButton: false,
                            timer: 2000,
                            allowOutsideClick: false
                        }).then(() => {
                            this.$router.push({
                                name: "my_orders",
                                query: {
                                    tab: "services"
                                }
                            });
                        });
                        this.service_request = {
                            message: '',
                            email: '',
                            location: '',
                            contact_number: '',
                            date: '',
                            product_id: null
                        };
                    } else {
                        Swal.fire({
                            title: 'Error',
                            text: response.message || 'Failed to submit service request',
                            icon: 'error',
                            position: 'bottom-end',
                            showConfirmButton: false,
                            timer: 3000,
                            toast: true
                        });
                    }
                },
                error: (xhr) => {
                    Swal.fire({
                        title: 'Error',
                        text: xhr.responseJSON?.message || 'Failed to submit service request',
                        icon: 'error',
                        position: 'bottom-end',
                        showConfirmButton: false,
                        timer: 3000,
                        toast: true
                    });
                },
                complete: () => {
                    this.isLoading = false;

                }
            });
        },
        build_visible_supplier(product) {
            const supplier = product?.base_profile?.company?.base_profile || product?.base_profile;
            const supplier_id = supplier?.id || product?.base_profile_id;
            const company_name = this.get_company_name(product);
            const logo_url = this.get_company_logo(product);

            return (!supplier_id || !company_name) ? null : {
                id: supplier_id,
                company_name,
                profile_type: supplier?.profile_type,
                logo_url,
            };
        },

        getSupplierIdFromProduct(product) {
            return String(
                product?.base_profile?.company?.base_profile?.id
                || product?.base_profile?.id
                || product?.base_profile_id
                || ''
            );
        },

        get_active_organization_id() {
            if (!this.is_organization_marketplace_enabled()) return '';

            return this.user_details?.organization_id
                || get_active_organization_id()
                || '';
        },

        is_organization_marketplace_enabled() {
            return is_enabled_feature(this.client_features?.show_organisation);
        },

        get_product_organization_id(product = {}) {
            return String(
                product?.company
            );
        },

        is_active_organization_product(product = {}) {
            const active_organization_id = String(this.get_active_organization_id() || '');
            const product_organization_id = this.get_product_organization_id(product);

            return !!active_organization_id
                && !!product_organization_id
                && product_organization_id === active_organization_id;
        },

        is_organization_scoped_product(product = {}) {
            return !!this.get_product_organization_id(product);
        },

        filter_visible_organization_products(products = []) {
            if (!this.is_organization_marketplace_enabled()) return products;

            return products.filter((product) =>
                !this.is_organization_scoped_product(product)
                || this.is_active_organization_product(product)
            );
        },

        filter_out_my_listings(products = []) {
            const current_user_id = String(this.user_details?.user_id || '');
            const visible_products = this.filter_visible_organization_products(products);
            if (!current_user_id) return visible_products;

            return visible_products.filter((product) =>
                String(product?.user?.id || '') !== current_user_id
                || this.is_active_organization_product(product)
            );
        },

        filter_products_by_supplier(products = []) {
            if (!this.filters.supplier_id) return products;

            return products.filter((product) =>
                this.getSupplierIdFromProduct(product) === String(this.filters.supplier_id)
            );
        },

        get_current_marketplace_owner_ids() {
            return [
                this.user_details?.base_profile_id,
                this.user_details?.active_base_profile_id,
                this.user_details?.profile_id,
                localStorage.getItem('active_profile_id'),
                localStorage.getItem('active_base_profile_id'),
                localStorage.getItem('base_profile_id'),
            ]
                .filter((id) => id !== undefined && id !== null && String(id).trim() !== '')
                .map((id) => String(id));
        },

        get_marketplace_product_owner_ids(product = {}) {
            return [
                product?.base_profile?.company?.base_profile?.id,
                product?.base_profile?.id,
                product?.base_profile_id,
            ]
                .filter((id) => id !== undefined && id !== null && String(id).trim() !== '')
                .map((id) => String(id));
        },

        is_my_marketplace_product(product = {}) {
            const current_user_id = String(this.user_details?.user_id || '');
            const product_user_id = String(product?.user?.id || product?.base_profile?.user?.id || '');
            const owner_ids = new Set(this.get_current_marketplace_owner_ids());

            return Boolean(
                (current_user_id && product_user_id && current_user_id === product_user_id)
                || this.get_marketplace_product_owner_ids(product).some((id) => owner_ids.has(id))
            );
        },


        get_product_category_name(product = {}) {
            return String(
                product?.category || ''
            ).trim().toLowerCase();
        },

        get_product_subcategory_name(product = {}) {
            return String( product?.subcategory|| ''
            ).trim().toLowerCase();
        },

        get_effective_category_filter_ids() {
            if (this.filters.subcategory_id) {
                return [String(this.filters.subcategory_id)];
            }

            if (!this.filters.category_id) return [];

            const child_subcategory_ids = this.get_subcategories_for_category(this.filters.category_id)
                .map((subcategory) => subcategory?.id)
                .filter((id) => id !== undefined && id !== null && id !== "")
                .map((id) => String(id));

            return child_subcategory_ids.length
                ? child_subcategory_ids
                : [String(this.filters.category_id)];
        },

        get_effective_category_filter_names() {
            return this.get_selected_category_filter_names()
                .filter(Boolean)
                .map((name) => String(name).trim().toLowerCase());
        },

        get_selected_category_filter_names() {
            if (this.filters.subcategory_id) {
                return [this.get_subcategory_filter_name()];
            }

            return [
                this.get_category_filter_name(),
                ...this.get_child_category_filter_names()
            ];
        },

        get_subcategory_filter_name() {
            return this.all_subcategories.find(
                (item) => String(item.id) === String(this.filters.subcategory_id)
            )?.name;
        },

        get_category_filter_name() {
            return this.all_categories.find(
                (item) => String(item.id) === String(this.filters.category_id)
            )?.name;
        },

        get_child_category_filter_names() {
            if (!this.filters.category_id) return [];

            return this.get_subcategories_for_category(this.filters.category_id)
                .map((subcategory) => subcategory?.name)
                .filter(Boolean);
        },

        filter_products_by_category(products = []) {
            const category_filter_ids = this.get_effective_category_filter_ids();
            const category_filter_names = this.get_effective_category_filter_names();
            return products.filter((product) => {
                if (!category_filter_ids.length && !category_filter_names.length) return true;
                const product_category_names = [
                    this.get_product_category_name(product),
                    this.get_product_subcategory_name(product),
                ].filter(Boolean);
                return product_category_names.some((name) => category_filter_names.includes(name));
            });
        },

        paginate_local_results(items = [], page = 1, page_size = 20) {
            const total_count = items.length;
            const total_pages = Math.max(1, Math.ceil(total_count / page_size));
            const safe_page = Math.min(Math.max(page, 1), total_pages);
            const start = (safe_page - 1) * page_size;
            const end = start + page_size;

            return ({
                items: items.slice(start, end),
                pagination: {
                    current_page: safe_page,
                    total_pages: total_pages,
                    next_page: safe_page < total_pages ? safe_page + 1 : null,
                    prev_page: safe_page > 1 ? safe_page - 1 : null,
                    total_count: total_count,
                    has_more: safe_page < total_pages
                }
            });
        },

        search_marketplace_products_request(payload) {
            return new Promise((resolve, reject) => {
                $.ajax({
                    url: `${process.env.VUE_APP_BACKEND_API_URL}/ads_management/search_marketplace/`,
                    type: 'POST',
                    data: JSON.stringify(payload),
                    headers: {
                        ...(this.token_exists ? { Authorization: `Token ${this.token_exists}` } : {}),
                        'Content-Type': 'application/json'
                    },
                    success: resolve,
                    error: reject
                });
            });
        },

        async fetch_all_search_results(payload) {
            const collected = [];
            let current_page = 1;
            let has_more = true;

            while (has_more) {
                const response = await this.search_marketplace_products_request({
                    ...payload,
                    page: current_page,
                    page_size: 100
                });

                if (response.status !== 'success') {
                    throw new Error(response.message || 'Failed to search marketplace');
                }

                const data = response.data || {};
                collected.push(...(data.results || []));
                has_more = Boolean(data.has_more);
                current_page += 1;
            }

            return collected;
        },

        on_cart_updated() {
            if (this.$refs.homeNavBarRef?.$refs.cartRef) {
                this.$refs.homeNavBarRef.$refs.cartRef.load_cart();
            }
        },

        open_create_business_profile() {
            setTimeout(() => {
                if (this.$refs.homeNavBarRef?.openCompanyProfileModal) {
                    this.$refs.homeNavBarRef.openCompanyProfileModal();
                }
            }, 200);
        },

        initial_wishlist_state() {
            if (!this.user_details.base_profile_id && !this.token_exists) return Promise.resolve();

            return new Promise((resolve) => {
                $.ajax({
                    url: `${process.env.VUE_APP_BACKEND_API_URL}/ads_management/get_wishlist_products/`,
                    method: 'GET',
                    data: { page: 1, page_size: 100 },
                    headers: { 'Authorization': `Token ${this.getToken()}` },
                    success: (response) => {
                        if (response.status === 'success') {
                            const wishlistItems = response.data || [];
                            this.wishlisted_product_ids = wishlistItems.map(item => item.id);
                        }
                        resolve();
                    },
                    error: () => {
                        console.error('Failed to init wishlist state');
                        resolve();
                    }
                });
            });
        },
        sync_wishlist_state() {
            const wishlistSet = new Set(this.wishlisted_product_ids);

            this.products.forEach(product => {
                product.is_wishlisted = wishlistSet.has(product.id);
            });

            if (this.selected_product) {
                this.selected_product.is_wishlisted = wishlistSet.has(this.selected_product.id);
            }
        },
        get_categories_for_department(department_id) {
            if (!department_id) return [];
            return this.all_categories.filter(c => {
                const c_dep_id = c.department?.id || c.department_id || c.department;
                return String(c_dep_id) === String(department_id);
            });
        },
        get_subcategories_for_category(category_id) {
            if (!category_id) return [];
            return this.all_subcategories.filter(s => {
                const s_cat_id = s.category?.id || s.category_id || s.category;
                return String(s_cat_id) === String(category_id);
            });
        },

        get_marketplace_tile_initials(name = '') {
            const words = String(name || '')
                .trim()
                .split(/\s+/)
                .map((word) => word.replace(/[^a-z0-9]/gi, ''))
                .filter(Boolean);
            if (!words.length) return 'MP';
            if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
            return `${words[0][0]}${words[1][0]}`.toUpperCase();
        },
        close_mega_menu() {
            this.hovered_department_id = null;
        },

        getToken() {
            return get_token();
        },
        scroll_departments(direction) {
            const container = this.$refs.marketplaceCategoryStrip || this.$refs.departmentsContainer;
            if (!container) return;

            const scroll_amount = Math.max(container.clientWidth * 0.75, 180);
            container.scrollBy({
                left: direction === 'left' ? -scroll_amount : scroll_amount,
                behavior: 'smooth'
            });
        },

        build_price_filters() {
            const priceRange = this.filters.price_range;

            if (!priceRange) return {};
            if (priceRange === '20000-plus') return { min_price: 20000 };

            const parts = priceRange.split('-');
            const min = Number(parts[0]);
            const max = Number(parts[1]);

            return ({
                min_price: min,
                max_price: max
            });
        },

        build_product_params(page = 1, page_size = 15) {
            const active_organization_id = this.get_active_organization_id();
            const user_id = this.user_details?.user_id || '';

            return ({
                page: page,
                page_size: page_size,
                ...(user_id ? { user_id } : {}),
                ...(active_organization_id ? {
                    organization_id: active_organization_id,
                } : {}),
                ...(this.filters.supplier_id ? { base_profile_id: this.filters.supplier_id } : {}),
                ...(this.filters.price_range ? { price_range: this.filters.price_range } : {}),
                ...(this.filters.department_id ? { department_id: this.filters.department_id } : {}),
                ...(this.filters.category_id ? { category_id: this.filters.category_id } : {}),
                ...(this.filters.subcategory_id ? { subcategory_id: this.filters.subcategory_id } : {})
            });
        },

        build_search_payload(page = 1) {
            const trimmed_search = this.filters.search ? this.filters.search.trim() : '';
            const price_filters = this.build_price_filters();
            const category_filter_ids = this.get_effective_category_filter_ids();
            const payload = {
                // Basic Search & Pagination
                ...(trimmed_search ? { query: trimmed_search } : {}),
                page: page,
                page_size: 12,

                // Add the location here
                road: this.filters.road,
                suburb: this.filters.suburb,
                city: this.filters.city,
                municipality: this.filters.municipality,
                state: this.filters.state,

                // Existing Filters
                ...(this.filters.supplier_id ? { base_profile_id: Number(this.filters.supplier_id) } : {}),

                ...price_filters
            };

            if (this.filters.department_id) {
                payload.department_id = Number(this.filters.department_id);
                payload.department = Number(this.filters.department_id);
            }

            if (this.filters.category_id) {
                payload.parent_category_id = Number(this.filters.category_id);
                payload.category_id = Number(category_filter_ids[0] || this.filters.category_id);
                payload.category = Number(category_filter_ids[0] || this.filters.category_id);
                payload.product_category_id = Number(category_filter_ids[0] || this.filters.category_id);
                payload.category_ids = category_filter_ids.map((id) => Number(id));
            }

            if (this.filters.subcategory_id) {
                payload.subcategory_id = Number(this.filters.subcategory_id);
                payload.subcategory = Number(this.filters.subcategory_id);
                payload.product_subcategory_id = Number(this.filters.subcategory_id);
            }

            return payload;
        },

        build_wishlist_pagination(pagination = {}, page = 1) {
            const total_pages = pagination?.total_pages || 1;
            return ({
                current_page: pagination?.current_page || page,
                total_pages: total_pages,
                next_page: pagination?.next_page || null,
                prev_page: pagination?.prev_page || null,
                total_count: pagination?.total_count || 0,
                has_more: page < total_pages
            });
        },

        handle_wishlist_products_success(response, page = 1) {
            if (response.status !== 'success') {
                if (page === 1) {
                    this.products = [];
                }
                this.error = response.message || 'Failed to fetch wishlist products';
                return;
            }

            const new_products = response.data || [];

            if (page === 1) {
                this.products = new_products;
            } else {
                this.products = this.products.concat(new_products);
            }

            this.pagination_products = this.build_wishlist_pagination(response.pagination, page);
        },

        fetch_marketplace_departments() {
            $.ajax({
                url: `${process.env.VUE_APP_BACKEND_API_URL}/ads_management/get_marketplace_departments/`,
                type: 'GET',
                headers: this.token_exists ? { Authorization: `Token ${this.token_exists}` } : {},
                success: (response) => {
                    if (response.status === 'success') {
                        const deps = response.data || [];
                        this.departments = deps;

                        let allCats = [];
                        let allSubCats = [];

                        deps.forEach(dep => {
                            const cats = dep.categories || [];
                            cats.forEach(cat => {
                                cat.department_id = dep.id;
                                allCats.push(cat);

                                const subCats = cat.subcategories || [];
                                subCats.forEach(sub => {
                                    sub.category_id = cat.id;
                                    allSubCats.push(sub);
                                });
                            });
                        });

                        this.all_categories = allCats;
                        this.all_subcategories = allSubCats;

                        this.fetch_marketplace_categories(this.filters.department_id);
                        this.fetch_marketplace_subcategories(this.filters.category_id);
                    } else {
                        this.departments = [];
                        this.all_categories = [];
                        this.all_subcategories = [];
                        this.categories = [];
                        this.subcategories = [];
                    }
                },
                error: (err) => {
                    console.error('Error fetching marketplace departments:', err);
                    this.departments = [];
                    this.all_categories = [];
                    this.all_subcategories = [];
                    this.categories = [];
                    this.subcategories = [];
                }
            });
        },

        fetch_marketplace_categories(departmentId = '') {
            if (!departmentId) {
                this.categories = this.all_categories;
            } else {
                this.categories = this.get_categories_for_department(departmentId);
            }
        },

        fetch_marketplace_subcategories(categoryId = '') {
            if (!categoryId) {
                this.subcategories = this.all_subcategories;
            } else {
                this.subcategories = this.get_subcategories_for_category(categoryId);
            }
        },

        begin_auto_paginate_cycle(page = 1, request_token = null) {
            if (request_token !== null && request_token !== undefined) {
                return request_token;
            }

            if (page === 1) {
                this.auto_paginate_token += 1;

                if (this.auto_paginate_timeout_id) {
                    clearTimeout(this.auto_paginate_timeout_id);
                    this.auto_paginate_timeout_id = null;
                }
            }

            return this.auto_paginate_token;
        },

        schedule_auto_paginate_next_page(fetch_callback, page, items_count, pagination, request_token) {
            if (!this.auto_paginate_without_scroll) return;
            if (request_token !== this.auto_paginate_token) return;
            if (typeof fetch_callback !== 'function') return;
            if (!items_count) return;

            const next_page = pagination?.next_page || (page + 1);
            const has_more = Boolean(
                pagination?.has_more
                || pagination?.next_page
                || (pagination?.total_pages && next_page <= pagination.total_pages)
            );

            if (!has_more || !next_page || next_page <= page) return;

            if (this.auto_paginate_timeout_id) {
                clearTimeout(this.auto_paginate_timeout_id);
            }

            const queue_next_page = () => {
                this.auto_paginate_timeout_id = setTimeout(() => {
                    if (request_token !== this.auto_paginate_token) return;
                    fetch_callback(next_page, request_token);
                }, 1500);
            };

            if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
                window.requestAnimationFrame(() => {
                    window.requestAnimationFrame(() => {
                        if (request_token !== this.auto_paginate_token) return;
                        queue_next_page();
                    });
                });
                return;
            }

            queue_next_page();
        },
        search_marketplace_products(page = 1, request_token = null) {
            request_token = this.begin_auto_paginate_cycle(page, request_token);
            this.showing_wishlist = false;

            // Only show loading on initial load
            if (page === 1) {
                this.loading_products = true;
                this.products = [];
                this.supplier_source_products = [];
            } else {
                this.infinite_scroll_loading = true;
            }

            this.error = null;

            const payload = this.build_search_payload(page);

            if (this.filters.supplier_id) {
                this.fetch_all_search_results(this.build_search_payload(1))
                    .then((all_results) => {
                        if (!this.is_current_marketplace_request(request_token)) return;
                        this.supplier_source_products = all_results;
                        const filtered_products = this.filter_products_by_supplier(all_results);
                        const paginated = this.paginate_local_results(filtered_products, page);

                        if (page === 1) {
                            this.products = paginated.items;
                        } else {
                            this.products = this.products.concat(paginated.items);
                        }
                        this.pagination_products = paginated.pagination;
                        this.schedule_auto_paginate_next_page(
                            this.search_marketplace_products.bind(this),
                            page,
                            paginated.items.length,
                            this.pagination_products,
                            request_token
                        );
                    })
                    .catch((err) => {
                        console.error('Error searching marketplace products:', err);
                        if (page === 1) {
                            this.products = [];
                        }
                        this.error = err?.responseJSON?.message || err?.message || 'Unable to search products. Please try again later.';
                    })
                    .finally(() => {
                        this.loading_products = false;
                        this.infinite_scroll_loading = false;
                        this.refresh_infinite_scroll();
                    });
                return;
            }

            $.ajax({
                url: `${process.env.VUE_APP_BACKEND_API_URL}/ads_management/search_marketplace/`,
                type: 'POST',
                data: JSON.stringify(payload),
                headers: {
                    ...(this.token_exists ? { Authorization: `Token ${this.token_exists}` } : {}),
                    'Content-Type': 'application/json'
                },
                success: (response) => {
                    if (!this.is_current_marketplace_request(request_token)) return;
                    if (response.status !== 'success') {
                        if (page === 1) {
                            this.products = [];
                        }
                        this.error = response.message || 'Failed to search marketplace';
                        return;
                    }

                    const data = response.data || {};
                    const new_results = data.results || [];

                    if (page === 1) {
                        this.products = new_results;
                        this.supplier_source_products = new_results;
                    } else {
                        this.products = this.products.concat(new_results);
                        this.supplier_source_products = this.supplier_source_products.concat(new_results);
                    }

                    this.pagination_products = {
                        current_page: data.page,
                        total_pages: data.total_pages,
                        next_page: data.has_more ? page + 1 : null,
                        prev_page: page > 1 ? page - 1 : null,
                        total_count: data.total_results,
                        has_more: data.has_more || false
                    };

                    this.schedule_auto_paginate_next_page(
                        this.search_marketplace_products.bind(this),
                        page,
                        new_results.length,
                        this.pagination_products,
                        request_token
                    );
                },
                error: (err) => {
                    console.error('Error searching marketplace products:', err);
                    if (page === 1) {
                        this.products = [];
                    }
                    this.error = err?.responseJSON?.message || 'Unable to search products. Please try again later.';
                },
                complete: () => {
                    this.loading_products = false;
                    this.infinite_scroll_loading = false;
                    this.refresh_infinite_scroll();
                }
            });
        },

        handle_marketplace_search_success(response, page) {
            if (response.status !== 'success') {
                this.handle_marketplace_search_failure(response.message, page);
                return;
            }

            this.apply_marketplace_search_results(response.data || {}, page);
        },

        handle_marketplace_search_failure(message, page) {
            if (page === 1) {
                this.products = [];
            }
            this.error = message || 'Failed to search marketplace';
        },

        apply_marketplace_search_results(data, page) {
            const new_results = data.results || [];

            if (page === 1) {
                this.products = new_results;
                this.supplier_source_products = new_results;
            } else {
                this.products = this.products.concat(new_results);
                this.supplier_source_products = this.supplier_source_products.concat(new_results);
            }

            this.pagination = {
                current_page: data.page,
                total_pages: data.total_pages,
                next_page: data.has_more ? page + 1 : null,
                prev_page: page > 1 ? page - 1 : null,
                total_count: data.total_results,
                has_more: data.has_more || false
            };
        },

        search_marketplace_services(page = 1, request_token = null) {
            request_token = this.begin_auto_paginate_cycle(page, request_token);
            this.showing_wishlist = false;

            // Only show loading on initial load
            if (page === 1) {
                this.loading_services = true;
                this.services = [];
                this.supplier_source_products = [];
            } else {
                this.infinite_scroll_loading = true;
            }

            this.error = null;

            const payload = this.build_search_payload(page);

            if (this.filters.supplier_id) {
                this.fetch_all_search_results(this.build_search_payload(1))
                    .then((all_results) => {
                        if (!this.is_current_marketplace_request(request_token)) return;
                        this.supplier_source_products = all_results;
                        const filtered_products = this.filter_products_by_supplier(all_results);
                        const paginated = this.paginate_local_results(filtered_products, page);

                        if (page === 1) {
                            this.services = paginated.items;
                        } else {
                            this.services = this.services.concat(paginated.items);
                        }
                        this.pagination_services = paginated.pagination;
                        this.schedule_auto_paginate_next_page(
                            this.search_marketplace_services.bind(this),
                            page,
                            paginated.items.length,
                            this.pagination_services,
                            request_token
                        );
                    })
                    .catch((err) => {
                        console.error('Error searching marketplace products:', err);
                        if (page === 1) {
                            this.services = [];
                        }
                        this.error = err?.responseJSON?.message || err?.message || 'Unable to search products. Please try again later.';
                    })
                    .finally(() => {
                        this.loading_services = false;
                        this.infinite_scroll_loading = false;
                        this.refresh_infinite_scroll();
                    });
                return;
            }

            $.ajax({
                url: `${process.env.VUE_APP_BACKEND_API_URL}/ads_management/search_marketplace_services/`,
                type: 'POST',
                data: JSON.stringify(payload),
                headers: {
                    ...(this.token_exists ? { Authorization: `Token ${this.token_exists}` } : {}),
                    'Content-Type': 'application/json'
                },
                success: (response) => {
                    if (!this.is_current_marketplace_request(request_token)) return;
                    if (response.status !== 'success') {
                        if (page === 1) {
                            this.products = [];
                        }
                        this.error = response.message || 'Failed to search marketplace';
                        return;
                    }

                    const data = response.data || {};
                    const new_results = data.results || [];

                    if (page === 1) {
                        this.services = new_results;
                        this.supplier_source_products = new_results;
                    } else {
                        this.services = this.services.concat(new_results);
                        this.supplier_source_products = this.supplier_source_products.concat(new_results);
                    }

                    this.pagination_services = {
                        current_page: data.page,
                        total_pages: data.total_pages,
                        next_page: data.has_more ? page + 1 : null,
                        prev_page: page > 1 ? page - 1 : null,
                        total_count: data.total_results,
                        has_more: data.has_more || false
                    };

                    this.schedule_auto_paginate_next_page(
                        this.search_marketplace_services.bind(this),
                        page,
                        new_results.length,
                        this.pagination_services,
                        request_token
                    );
                },
                error: (err) => {
                    console.error('Error searching marketplace products:', err);
                    if (page === 1) {
                        this.products = [];
                    }
                    this.error = err?.responseJSON?.message || 'Unable to search products. Please try again later.';
                },
                complete: () => {
                    this.loading_services = false;
                    this.infinite_scroll_loading = false;
                    this.refresh_infinite_scroll();
                }
            });
        },
        fetch_wishlist_products(page = 1, request_token = null) {
            request_token = this.begin_auto_paginate_cycle(page, request_token);
            this.showing_wishlist = true;

            // Only show loading on initial load
            if (page === 1) {
                this.loading_products = true;
                this.products = [];
            } else {
                this.infinite_scroll_loading = true;
            }

            this.error = null;

            $.ajax({
                url: `${process.env.VUE_APP_BACKEND_API_URL}/ads_management/get_wishlist_products/`,
                method: 'GET',
                data: {
                    page: page,
                    page_size: 12
                },
                headers: {
                    'Authorization': `Token ${this.getToken()}`
                },
                success: (response) => {
                    if (!this.is_current_marketplace_request(request_token)) return;
                    this.handle_wishlist_products_success(response, page);
                    if (page === 1) {
                        this.supplier_source_products = response.data || [];
                    } else {
                        this.supplier_source_products = (this.supplier_source_products || []).concat(response.data || []);
                    }

                    const new_products = response.data || [];
                    this.schedule_auto_paginate_next_page(
                        this.fetch_wishlist_products.bind(this),
                        page,
                        new_products.length,
                        this.pagination_products,
                        request_token
                    );
                },
                error: (xhr) => {
                    console.error('Error fetching wishlist products:', xhr);
                    if (page === 1) {
                        this.products = [];
                    }
                    this.error = xhr?.responseJSON?.message || 'Unable to load wishlist products. Please try again later.';
                },
                complete: () => {
                    this.loading_products = false;
                    this.infinite_scroll_loading = false;
                    this.refresh_infinite_scroll();
                }
            });
        },

        async handle_marketplace_action(page = 1) {
            $('#filtersOffcanvas .btn[data-bs-dismiss="offcanvas"]').click();

            if (this.active_listing_type === 'services' && !this.filters.location) {
                try {
                    await this.get_user_location();
                } catch (err) {
                    console.warn("Could not get location, searching globally.");
                }
            }

            if (this.active_listing_type === 'services') {
                return this.search_marketplace_services(page);
            }

            if (this.filters.search && this.filters.search.trim()) {
                return this.search_marketplace_products(page);
            }

            return this.fetch_marketplace_products(page);
        },

        on_filter_change() {
            this.filters.search = '';
            this.handle_marketplace_action(1);
            $('#filtersOffcanvas .btn[data-bs-dismiss="offcanvas"]').click();
        },

        async select_department(departmentId) {
            this.filters.search = '';
            this.filters.department_id = departmentId ? String(departmentId) : '';
            this.filters.category_id = '';
            this.filters.subcategory_id = '';
            this.fetch_marketplace_categories(this.filters.department_id);
            this.fetch_marketplace_subcategories();
            this.handle_marketplace_action(1);
        },

        async on_department_change() {
            this.filters.search = '';
            this.filters.category_id = '';
            this.filters.subcategory_id = '';
            this.fetch_marketplace_categories(this.filters.department_id);
            this.fetch_marketplace_subcategories();
        },

        async select_category(categoryId) {
            this.filters.search = '';
            this.filters.category_id = categoryId ? String(categoryId) : '';
            this.filters.subcategory_id = '';
            this.fetch_marketplace_subcategories(this.filters.category_id);
            this.handle_marketplace_action(1);
        },

        async on_category_change() {
            this.filters.search = '';
            this.filters.subcategory_id = '';
            this.fetch_marketplace_subcategories(this.filters.category_id);
            this.handle_marketplace_action(1);
        },

        async select_subcategory(subcategoryId) {
            this.filters.search = '';
            this.filters.subcategory_id = subcategoryId ? String(subcategoryId) : '';
            this.handle_marketplace_action(1);
        },

        async on_subcategory_change() {
            this.filters.search = '';
            this.handle_marketplace_action(1);
        },

        set_price_range(priceRange) {
            this.filters.search = '';
            this.filters.price_range = priceRange;
            this.handle_marketplace_action(1);
        },

        clear_search() {
            this.filters.search = '';
            this.handle_marketplace_action(1);
        },

        set_listing_type(type) {
            this.active_listing_type = type === 'services' ? 'services' : 'products';
            this.$nextTick(() => {
                this.refresh_infinite_scroll();
            });
        },

        go_to_owner_catalogue() {
            this.$router.push({
                name: this.active_listing_type === 'services' ? 'my_services' : 'my_shop',
                query: {
                    open_actions: '1'
                }
            });
        },

        filter_by_supplier(supplier) {
            if (!supplier?.id) return;

            this.selected_supplier = supplier;
            this.filters.search = '';
            this.filters.supplier_id = String(supplier.id);
            this.handle_marketplace_action(1);
        },

        view_profile(profile) {
            view_profile_helper(this.$router, profile);
        },

        clear_supplier_filter() {
            this.selected_supplier = null;
            this.filters.supplier_id = '';
            this.handle_marketplace_action(1);
        },

        async clear_department_filter() {
            this.filters.department_id = '';
            this.filters.category_id = '';
            this.filters.subcategory_id = '';
            this.fetch_marketplace_categories();
            this.fetch_marketplace_subcategories();
            this.handle_marketplace_action(1);
        },

        clear_category_filter() {
            this.filters.category_id = '';
            this.filters.subcategory_id = '';
            this.fetch_marketplace_subcategories();
            this.handle_marketplace_action(1);
        },

        clear_subcategory_filter() {
            this.filters.subcategory_id = '';
            this.handle_marketplace_action(1);
        },

        clear_price_filter() {
            this.filters.price_range = '';
            this.handle_marketplace_action(1);
        },

        async clear_filters() {
            this.filters = {
                search: '',
                price_range: '',
                supplier_id: '',
                department_id: '',
                category_id: '',
                subcategory_id: '',
            };
            this.selected_supplier = null;
            this.fetch_marketplace_categories();
            this.fetch_marketplace_subcategories();
            this.handle_marketplace_action(1);
        },

        change_page(page) {
            if (page < 1 || page > this.current_pagination.total_pages) return;

            this.handle_marketplace_action(page);
        },

        setup_infinite_scroll() {
            if (this.auto_paginate_without_scroll) {
                if (this.intersection_observer) {
                    this.intersection_observer.disconnect();
                    this.intersection_observer = null;
                }
                return;
            }

            const sentinel = this.$refs.infiniteScrollSentinel;
            if (!sentinel) return;

            const options = {
                root: null,
                rootMargin: '100px',
                threshold: 0.1
            };

            this.intersection_observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && this.current_pagination.has_more && !this.infinite_scroll_loading && !this.is_loading) {
                        this.load_next_page();
                    }
                });
            }, options);

            this.intersection_observer.observe(sentinel);
        },

        refresh_infinite_scroll() {
            if (this.intersection_observer) {
                this.intersection_observer.disconnect();
                this.intersection_observer = null;
            }

            if (this.auto_paginate_without_scroll) {
                return;
            }

            this.$nextTick(() => {
                this.setup_infinite_scroll();
            });
        },

        load_next_page() {
            if (this.auto_paginate_without_scroll) return;

            const next_page = this.current_pagination.current_page + 1;

            if (!this.current_pagination.has_more) return;

            if (this.filters.search && this.filters.search.trim()) {
                if (this.active_listing_type === 'services') {
                    this.search_marketplace_services(next_page);
                    return;
                }

                this.search_marketplace_products(next_page);
            } else if (this.showing_wishlist) {
                this.fetch_wishlist_products(next_page);
            } else {
                if (this.active_listing_type === 'services') {
                    this.fetch_marketplace_services(next_page);
                    return;
                }

                this.fetch_marketplace_products(next_page);
            }
        },

        add_product_to_cart(product) {
            if (!product) return;
            if (this.is_my_marketplace_product(product)) return;

            this.attribute_errors = {};

            if (this.product_has_selectable_attributes(product)) {
                this.open_product_modal(product);
                return;
            }

            this.add_to_cart(product);

        },

        async add_to_cart(product) {
            if (!product) return;
            if (this.is_my_marketplace_product(product)) return;

            const token = this.getToken();

            if (!token) {
                window.location = "/register";
                return;
            }

            const quantity = Number(product.quantity) > 0
                ? Number(product.quantity)
                : 1;
            const selected_attribute_ids = extract_selected_attribute_ids(product.attributes);

            const base_profile_id = this.get_product_base_profile_id(product);

            if (!base_profile_id) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Unable to identify product vendor',
                    showConfirmButton: false,
                    timer: 1500,
                    toast: true,
                    position: 'bottom-end'
                });
                return;
            }

            try {
                const response = await add_to_cart_helper({
                    product_id: product.id,
                    base_profile_id,
                    token,
                    quantity,
                    attributes: selected_attribute_ids.length ? selected_attribute_ids : []

                });

                if (response.status === 'success') {
                    this.$emit('cart_updated');
                    this.on_cart_updated();
                    Swal.fire({
                        icon: 'success',
                        title: response.message || 'Added to cart',
                        showConfirmButton: false,
                        timer: 1500,
                        toast: true,
                        position: 'bottom-end'
                    });
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: response.message || 'Failed to add product to cart',
                        showConfirmButton: false,
                        timer: 1500,
                        toast: true,
                        position: 'bottom-end'
                    });
                }
            } catch (err) {
                console.error('Error adding product to cart:', err);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: err?.responseJSON?.message || 'Failed to add product to cart',
                    showConfirmButton: false,
                    timer: 1500,
                    toast: true,
                    position: 'bottom-end'
                });
            }
        },

        get_product_base_profile_id(product) {
            return get_marketplace_product_base_profile_id(product);
        },

        increase_modal_quantity() {
            if (this.is_selected_product_quantity_disabled) return;

            this.selected_quantity += 1;
        },

        decrease_modal_quantity() {
            if (this.is_selected_product_quantity_disabled) return;

            if (this.selected_quantity > 1) {
                this.selected_quantity -= 1;
            }
        },
        go_to_previous_image() {
            if (!this.active_images?.length) return;

            const total = this.active_images.length;

            this.active_image_index =
                (this.active_image_index - 1 + total) % total;
        },

        go_to_next_image() {
            if (!this.active_images?.length) return;

            const total = this.active_images.length;

            this.active_image_index =
                (this.active_image_index + 1) % total;
        },

        go_to_image(index) {
            this.active_image_index = index;
        },

        add_selected_product_to_cart() {
            if (!this.selected_product) return;
            if (this.is_my_marketplace_product(this.selected_product)) return;

            this.attribute_errors = {};

            const validation = validate_marketplace_required_attributes(
                this.get_required_attribute_groups(this.selected_product.attributes || []),
                this.selected_attributes
            );
            this.attribute_errors = validation.attribute_errors;

            if (!validation.valid) {

                if (!$('#product_details_modal').hasClass('show')) {
                    $('#product_details_modal').modal('show');
                }

                return;
            }

            // 🚨 CONVERT OBJECT → ARRAY OF IDS
            const attributes = Object.values(this.selected_attributes || {});

            const product = {
                ...this.selected_product,
                quantity: this.selected_quantity,
                attributes: attributes   // ✔ FIXED
            };
            $('#product_details_modal').modal('hide');

            this.add_to_cart(product);

            this.reset_product_select_state();
        },


        async buy_selected_product() {
            if (!this.selected_product) return;
            if (this.is_my_marketplace_product(this.selected_product)) return;

            this.attribute_errors = {};

            const validation = validate_marketplace_required_attributes(
                this.get_required_attribute_groups(this.selected_product.attributes || []),
                this.selected_attributes
            );
            this.attribute_errors = validation.attribute_errors;

            if (!validation.valid) return;
            // 🚨 CONVERT OBJECT → ARRAY OF IDS
            const attributes = Object.values(this.selected_attributes || {});

            const token = this.getToken();
            if (!token) {
                window.location = "/register";
                return;
            }

            try {
                const checkout = await prepare_marketplace_buy_now_checkout({
                    product: this.selected_product,
                    token,
                    quantity: this.selected_quantity,
                    attributes
                });

                $('#product_details_modal').modal('hide');

                this.$router.push({
                    name: 'cart_checkout',
                    params: { checkout_slug: checkout.checkout_slug }
                });
            } catch (error) {
                console.error('Error preparing buy now checkout:', error);

                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error?.message || 'Failed to proceed to checkout',
                    timer: 1800,
                    toast: true,
                    position: 'bottom-end',
                    showConfirmButton: false
                });
            }
        },


        async fetch_marketplace_product_details(product_id) {
            const response = await axios.post(
                `${process.env.VUE_APP_BACKEND_API_URL}/ads_management/get_product/`,
                { product_id, user_id: this.user_details?.user_id || null },
                {
                    headers: {
                        ...(this.token_exists ? { Authorization: `Token ${this.token_exists}` } : {})
                    }
                }
            );

            if (response.data?.status !== 'success') {
                throw new Error(response.data?.message || 'Failed to load product details');
            }

            return response.data.data;
        },

        async open_product_modal(product) {
            if (!product) return;

            try {
                if (!product.__details_loaded) {
                    const product_details = await this.fetch_marketplace_product_details(product.id);
                    product = {
                        ...product,
                        ...product_details,
                        __details_loaded: true
                    };

                    this.products = this.products.map((item) =>
                        item.id === product.id ? product : item
                    );
                    this.services = this.services.map((item) =>
                        item.id === product.id ? product : item
                    );
                }
            } catch (error) {
                console.error('Failed to hydrate product details:', error);
            }

            const contact_details = get_service_request_prefill(this.user_details);

            this.selected_product = product;
            this.active_image_index = 0;
            this.new_rating = 0;
            this.hover_rating = 0;
            this.product_review_text = '';
            this.selected_quantity = 1;
            this.selected_attributes = {};
            this.attribute_errors = {};
            this.selected_attributes = auto_select_single_product_attribute_options(
                this.get_required_attribute_groups(product?.attributes || []),
                this.selected_attributes,
                is_valid_product_attribute_option
            );
            this.user_has_rated_product = false;
            this.product_reviews = product.reviews || [];
            if (this.selected_product.product_type.name.toLowerCase() !== 'product') {
                this.errors = {};
                this.service_request = product;
                this.service_request.product_id = product.id;
                this.service_request.email = contact_details.email;
                this.service_request.contact_number = contact_details.contact_number;
                this.service_request.message = '';
                this.service_request.location = '';
                this.service_request.date = '';
                this.service_availability_week_offset = 0;
                this.selected_service_availability_month = this.min_service_availability_month;
                this.selected_service_request_day_key = this.service_availability_days[0]?.date_key || '';

                this.$nextTick(() => {
                    if (this.service_request?.service_details?.type_name === 'on_site') {
                        this.auto_complete_map();
                    }
                });
                $('#service_request_modal').modal('show');
            }
            else {
                $('#product_details_modal').modal('show');
            }

        },


        submit_product_review() {
            if (!this.new_rating || !this.product_review_text.trim()) return

            $.ajax({
                url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/create_product_review/`,
                type: 'POST',
                headers: {
                    Authorization: `Token ${get_token()}`,
                    'Content-Type': 'application/json',
                },
                data: JSON.stringify({
                    product_id: this.selected_product.id,
                    rating: this.new_rating,
                    review: this.product_review_text,
                }),
                success: (response) => {
                    if (response.status === 'success') {
                        this.selected_product.user_has_rated_product = true;
                        this.user_has_rated_product = true;
                        if (response.data && response.data.product_rating) {
                            this.product_reviews.unshift(response.data.product_rating);
                        }
                        if (response.data && response.data.average_rating) {
                            this.selected_product.average_rating = response.data.average_rating;
                        }
                        this.new_rating = 0;
                        this.hover_rating = 0;
                        this.product_review_text = '';
                        Swal.fire({
                            icon: 'success',
                            title: 'Review Submitted',
                            text: 'Thank you for your feedback!',
                            showConfirmButton: false,
                            timer: 1500,
                            position: 'bottom-end',
                            toast: true
                        });
                    }
                }
            });
        },
        format_date(datestr) {
            if (!datestr) return '';
            const date = new Date(datestr);
            const day = date.getDate();
            const suffix = (d => {
                if (d > 3 && d < 21) return 'th';
                switch (d % 10) {
                    case 1: return 'st';
                    case 2: return 'nd';
                    case 3: return 'rd';
                    default: return 'th';
                }
            })(day);

            const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
            const month = date.toLocaleDateString('en-US', { month: 'long' });
            const year = date.getFullYear();

            return `${weekday}, ${month} ${day}${suffix}, ${year}`;
        },
        async toggle_wishlist(product) {
            // Get the vendor's base profile ID associated with the product
            const base_profile_id = product.base_profile?.company?.base_profile?.id
                || product.base_profile?.id || product.base_profile_id;

            if (!base_profile_id) {
                console.error('Base profile ID not found for the product');
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Unable to identify product vendor',
                    showConfirmButton: false,
                    timer: 1500,
                    toast: true,
                    position: 'bottom-end'
                });
                return;
            }

            $.ajax({
                url: `${process.env.VUE_APP_BACKEND_API_URL}/ads_management/toggle_wishlist/`,
                type: 'POST',
                headers: {
                    Authorization: `Token ${get_token()}`,
                    'Content-Type': 'application/json'
                },
                data: JSON.stringify({
                    product_id: product.id,
                    base_profile_id: base_profile_id
                }),
                success: (response) => {
                    if (response.status === 'success') {
                        const isWishlisted = this.wishlisted_product_ids.includes(product.id);

                        this.wishlisted_product_ids = isWishlisted

                            ? this.wishlisted_product_ids.filter(id => id !== product.id)
                            : [...this.wishlisted_product_ids, product.id];

                        if (this.showing_wishlist && isWishlisted) {
                            this.products = this.products.filter(p => p.id !== product.id);
                        }

                        Swal.fire({
                            icon: 'success',
                            title: response.message,
                            showConfirmButton: false,
                            timer: 1500,
                            toast: true,
                            position: 'bottom-end'
                        });
                    }
                },
                error: (err) => {
                    console.error('Failed to toggle wishlist:', err);
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: err?.responseJSON?.message || 'Failed to update wishlist',
                        showConfirmButton: false,
                        timer: 1500,
                        toast: true,
                        position: 'bottom-end'
                    });
                }
            });
        },

        view_product_details(product) {
            view_profile_product(this.$router, product);
        },

        get_company_logo(product) {
            return get_marketplace_company_logo(product);
        },

        get_company_name(product) {
            return get_marketplace_company_name(product);
        },

        get_company_initials(product) {
            return get_marketplace_company_initials(product);
        },

        get_supplier_initials(name) {
            if (!name) return "SU";

            return name
                .split(" ")
                .filter(Boolean)
                .slice(0, 2)
                .map(part => part.charAt(0).toUpperCase())
                .join("");
        },

        format_price(price) {
            return format_marketplace_price(price);
        },
        // REFACTORED VERSION
        async fetch_marketplace_services(page = 1, request_token = null) {
            request_token = this.begin_auto_paginate_cycle(page, request_token);
            this.loading_services = true;

            // 1. Guard Clause: Local Filtering (Does not need the API try/catch)
            if (this.filters.supplier_id) {
                try {
                    const all = await this.fetch_all_services_raw(this.build_product_params(1));
                    if (!this.is_current_marketplace_request(request_token)) return;
                    const filtered = this.filter_products_by_supplier(all);
                    const paginated = this.paginate_local_results(filtered, page);

                    this.services = page === 1 ? paginated.items : this.services.concat(paginated.items);
                    this.pagination_services = paginated.pagination;
                    this.schedule_auto_paginate_next_page(
                        this.fetch_marketplace_services.bind(this),
                        page,
                        paginated.items.length,
                        this.pagination_services,
                        request_token
                    );
                } catch (err) {
                    this.error = "Failed to load supplier services.";
                } finally {
                    this.loading_services = false;
                    this.infinite_scroll_loading = false;
                    this.refresh_infinite_scroll();
                }
                return; // Exit early
            }

            // 2. Standard API Fetch Logic
            try {
                const response = await axios.get(
                    `${process.env.VUE_APP_BACKEND_API_URL}/ads_management/get_marketplace_services/`,
                    { params: this.build_product_params(page) }
                );

                if (response.data.status === 'success') {
                    if (!this.is_current_marketplace_request(request_token)) return;
                    const data = response.data.data || [];
                    this.services = page === 1 ? data : this.services.concat(data);
                    this.pagination_services = {
                        ...response.data.pagination,
                        has_more: page < response.data.pagination.total_pages
                    };
                    this.schedule_auto_paginate_next_page(
                        this.fetch_marketplace_services.bind(this),
                        page,
                        data.length,
                        this.pagination_services,
                        request_token
                    );
                }
            } catch (err) {
                this.error = "Failed to load services.";
            } finally {
                this.loading_services = false;
                this.infinite_scroll_loading = false;
                this.refresh_infinite_scroll();
            }
        },
        async fetch_all_services_raw(params) {
            const collected = [];
            let current_page = 1;
            let total_pages = 1;
            do {
                const res = await axios.get(`${process.env.VUE_APP_BACKEND_API_URL}/ads_management/get_marketplace_services/`, {
                    params: { ...params, page: current_page, page_size: 100 }
                });
                collected.push(...(res.data.data || []));
                total_pages = res.data.pagination?.total_pages || 1;
                current_page++;
            } while (current_page <= total_pages);
            return collected;
        },
        async fetch_marketplace_products(page = 1, request_token = null) {
            request_token = this.begin_auto_paginate_cycle(page, request_token);
            this.showing_wishlist = false;
            if (page === 1) {
                this.loading_products = true;
                this.products = [];
            } else {
                this.infinite_scroll_loading = true;
            }

            try {
                // Special logic if filtering by a specific supplier locally
                if (this.filters.supplier_id) {
                    const all = await this.fetch_all_products_raw(this.build_product_params(1, MARKETPLACE_PRODUCTS_PAGE_SIZE));
                    if (!this.is_current_marketplace_request(request_token)) return;
                    const filtered = this.filter_products_by_supplier(all);
                    const paginated = this.paginate_local_results(filtered, page, MARKETPLACE_PRODUCTS_PAGE_SIZE);
                    this.products = page === 1 ? paginated.items : this.products.concat(paginated.items);
                    this.pagination_products = paginated.pagination;
                    this.schedule_auto_paginate_next_page(
                        this.fetch_marketplace_products.bind(this),
                        page,
                        paginated.items.length,
                        this.pagination_products,
                        request_token
                    );
                    return;
                }

                const response = await fetch_cached_marketplace_products(
                    this.build_product_params(page, MARKETPLACE_PRODUCTS_PAGE_SIZE)
                );

                if (response.status === 'success') {
                    if (!this.is_current_marketplace_request(request_token)) return;
                    const data = response.data || [];
                    this.products = page === 1 ? data : this.products.concat(data);
                    this.pagination_products = {
                        ...response.pagination,
                        has_more: response.pagination?.has_more ?? page < response.pagination?.total_pages
                    };
                    this.schedule_auto_paginate_next_page(
                        this.fetch_marketplace_products.bind(this),
                        page,
                        data.length,
                        this.pagination_products,
                        request_token
                    );
                }
            } catch (err) {
                this.error = "Failed to load products.";
            } finally {
                this.loading_products = false;
                this.infinite_scroll_loading = false;
                this.refresh_infinite_scroll();
            }
        },

        async fetch_all_products_raw(params) {
            const collected = [];
            let current_page = 1;
            let total_pages = 1;
            do {
                const response = await fetch_cached_marketplace_products({
                    ...params,
                    page: current_page,
                    page_size: 100
                });
                collected.push(...(response.data || []));
                total_pages = response.pagination?.total_pages || 1;
                current_page++;
            } while (current_page <= total_pages);
            return collected;
        },
        select_attribute(type, attr) {
            const next_state = select_marketplace_attribute({
                selected_attributes: this.selected_attributes,
                attribute_errors: this.attribute_errors,
                type,
                attribute: attr
            });

            this.selected_attributes = next_state.selected_attributes;
            this.attribute_errors = next_state.attribute_errors;
        },
        group_attributes(attributes) {
            return group_marketplace_attributes(attributes);
        },

        get_required_attribute_groups(attributes = []) {
            return get_required_marketplace_attribute_groups(
                attributes,
                is_valid_product_attribute_option
            );
        },

        product_has_selectable_attributes(product) {
            return this.get_required_attribute_groups(product?.attributes || []).length > 0;
        },

        reset_product_select_state() {
            this.selected_attributes = {};
            this.attribute_errors = {};
            this.selected_quantity = 1;
        }
    }
};
